import React, { useCallback, useEffect, useRef, useState } from "react";
import { api } from "./api";

// "Reset test data" (build plan §8A.3). Test environment only: the nav item
// and every route behind it are absent from the live app. Any signed-in test
// user may reset. Before resetting it shows who else has used the test app in
// the last 30 minutes and requires typing RESET; then a progress bar, from
// polling the server's status (no websockets). Newest first; the one the guide
// names (Config TestRecommendedPack) on top, "Recommended for testing", the
// rest under "More starting points", collapsed.

interface Pack {
  name: string;
  label: string;
  description: string;
}
interface Step {
  key: string;
  label: string;
  done: number;
  total: number | null;
}
interface Status {
  state: "idle" | "running" | "done" | "failed";
  pack: string | null;
  actorName: string | null;
  percent: number;
  steps: Step[];
  error: string | null;
  /** The closing roster read failed (the Reset itself finished). */
  rosterError?: string | null;
}

const POLL_MS = 1000;

function namesSentence(names: string[]): string {
  if (names.length === 1) return `${names[0]} is`;
  if (names.length === 2) return `${names[0]} and ${names[1]} are`;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]} are`;
}

function Progress({ status, packs }: { status: Status; packs: Pack[] }) {
  const current = status.steps.find((s) => s.total === null || s.done < s.total);
  const label = packs.find((p) => p.name === status.pack)?.label ?? status.pack;
  return (
    <div className="mt-4" aria-live="polite">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="min-w-0 truncate font-medium">
          {status.state === "running" ? `Resetting to "${label}"…` : status.state === "done" ? `Reset to "${label}" finished` : "Reset failed"}
        </span>
        <span className="shrink-0 font-mono">{status.percent}%</span>
      </div>
      <div
        className="mt-2 h-3 w-full overflow-hidden rounded-full bg-slate-200"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={status.percent}
        aria-label="Reset progress"
      >
        <div
          className={`h-full rounded-full transition-all ${status.state === "failed" ? "bg-red-600" : "bg-program"}`}
          style={{ width: `${status.percent}%` }}
        />
      </div>
      {status.state === "running" && current && (
        <p className="mt-2 truncate text-xs text-slate-600">
          {current.label}
          {current.total ? ` (${current.done} of ${current.total})` : ""}
        </p>
      )}
      {status.state === "done" && (
        <p className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900" role="status">
          Done. The test data is now "{label}". Everyone's test sign-ins and PINs still work.
        </p>
      )}
      {status.state === "done" && status.rosterError && (
        <p className="field-error" role="alert">
          The roster could not be read afterwards ({status.rosterError}). A System Admin can read it again from the Roster screen.
        </p>
      )}
      {status.state === "failed" && (
        <p className="field-error" role="alert">
          {status.error ?? "The reset did not finish."} Some test data may already be cleared; run the reset again.
        </p>
      )}
    </div>
  );
}

function PackChoice({ p, pack, setPack, recommended }: { p: Pack; pack: string; setPack: (n: string) => void; recommended?: boolean }) {
  return (
    <label className={`flex cursor-pointer gap-3 rounded-md border p-3 ${pack === p.name ? "border-program bg-slate-50" : "border-slate-200"}`}>
      <input type="radio" name="pack" value={p.name} checked={pack === p.name} onChange={() => setPack(p.name)} className="mt-1" />
      <span className="min-w-0">
        <span className="block text-sm font-medium">
          {p.label}
          {recommended && <span className="ml-2 rounded bg-emerald-100 px-1.5 py-0.5 text-xs font-semibold text-emerald-900">Recommended for testing</span>}
        </span>
        <span className="block text-sm text-slate-600">{p.description}</span>
      </span>
    </label>
  );
}

export function TestReset() {
  const [packs, setPacks] = useState<Pack[]>([]);
  const [recommended, setRecommended] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const [testers, setTesters] = useState<string[] | null>(null);
  const [pack, setPack] = useState<string>("");
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<Status | null>(null);
  const timer = useRef<number | null>(null);

  const poll = useCallback(async () => {
    const r = await api<{ status: Status }>("GET", "/api/test/reset/status");
    if (r.ok) {
      setStatus(r.data.status);
      if (r.data.status.state === "running") timer.current = window.setTimeout(() => void poll(), POLL_MS);
    } else {
      timer.current = window.setTimeout(() => void poll(), POLL_MS * 3);
    }
  }, []);

  useEffect(() => {
    void api<{ packs: Pack[]; recommended: string }>("GET", "/api/test/packs").then((r) => {
      if (r.ok) {
        setPacks(r.data.packs);
        setRecommended(r.data.recommended);
        if (r.data.packs.some((p) => p.name === r.data.recommended)) setPack(r.data.recommended);
      }
      else setError(r.data.error ?? "Couldn't load the starting points.");
    });
    void api<{ testers: { name: string }[] }>("GET", "/api/test/active-testers").then((r) => {
      if (r.ok) setTesters(r.data.testers.map((t) => t.name));
    });
    void poll(); // pick up a reset already running (another tab, another tester)
    return () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    };
  }, [poll]);

  async function start(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const r = await api<{ status: Status }>("POST", "/api/test/reset", { pack, confirm: typed });
    if (r.status === 202) {
      setStatus(r.data.status);
      setTyped("");
      timer.current = window.setTimeout(() => void poll(), POLL_MS);
    } else setError(r.data.error ?? "The reset didn't start.");
  }

  const running = status?.state === "running";
  const ready = Boolean(pack) && typed === "RESET" && !running;

  return (
    <form className="section-card" onSubmit={start} noValidate>
      <h2 className="text-lg font-semibold">Reset test data</h2>
      <p className="mt-1 text-sm text-slate-600">
        Puts the test environment back to a starting point. Every test record, note, setting and uploaded file is
        erased and replaced. Test users and PINs are kept. Nothing here touches the live app.
      </p>

      <fieldset className="mt-4" disabled={running}>
        <legend className="field-label">Starting point</legend>
        <div className="space-y-2">
          {packs.filter((p) => p.name === recommended).map((p) => <PackChoice key={p.name} p={p} pack={pack} setPack={setPack} recommended />)}
        </div>
        {packs.some((p) => p.name !== recommended) && (
          <>
            <button type="button" className="mt-2 text-sm text-program underline" aria-expanded={more} aria-controls="more-packs" onClick={() => setMore(!more)}>
              {more ? "Hide the other starting points" : "More starting points"}
            </button>
            {more && (
              <div id="more-packs" className="mt-2 space-y-2">
                {packs.filter((p) => p.name !== recommended).map((p) => <PackChoice key={p.name} p={p} pack={pack} setPack={setPack} />)}
              </div>
            )}
          </>
        )}
      </fieldset>

      <div className="mt-4 text-sm" role="note">
        {testers === null ? (
          <p className="text-slate-500">Checking who else is testing…</p>
        ) : testers.length ? (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-amber-950">
            <b>{namesSentence(testers)} also testing</b> (active in the last 30 minutes). Resetting erases their work
            too.
          </p>
        ) : (
          <p className="text-slate-600">No one else has used the test app in the last 30 minutes.</p>
        )}
      </div>

      <label className="field-label mt-4" htmlFor="confirm-reset">
        Type RESET to confirm
      </label>
      <input
        id="confirm-reset"
        className="field-input max-w-[12rem] font-mono tracking-widest"
        value={typed}
        onChange={(e) => setTyped(e.target.value)}
        autoComplete="off"
        disabled={running}
      />
      {error && <p className="field-error" role="alert">{error}</p>}
      <div className="mt-4">
        <button type="submit" className="btn-primary w-full sm:w-auto" disabled={!ready}>
          {running ? "Resetting…" : "Reset test data"}
        </button>
      </div>

      {status && status.state !== "idle" && <Progress status={status} packs={packs} />}
    </form>
  );
}
